import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? window.location.origin : '';

/**
 * CreatorChatDashboard - Dashboard de chat para criadores
 * 3 tipos: Anúncios, Assinantes, Chat Privado Pago
 */
const CreatorChatDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('broadcasts');
  const [loading, setLoading] = useState(false);
  
  // Estados para Broadcasts
  const [broadcasts, setBroadcasts] = useState([]);
  const [broadcastsToday, setBroadcastsToday] = useState(0);
  const [remainingToday, setRemainingToday] = useState(10);
  const [newBroadcast, setNewBroadcast] = useState({ content: '', image: null, postId: '' });
  
  // Estados para Assinantes
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [subscriberMessages, setSubscriberMessages] = useState([]);
  const [newSubscriberMessage, setNewSubscriberMessage] = useState('');
  
  // Estados para Chat Privado
  const [privateChatRequests, setPrivateChatRequests] = useState([]);
  const [activePrivateChats, setActivePrivateChats] = useState([]);
  const [selectedPrivateChat, setSelectedPrivateChat] = useState(null);
  const [privateMessages, setPrivateMessages] = useState([]);
  const [newPrivateMessage, setNewPrivateMessage] = useState('');
  const [priceInput, setPriceInput] = useState('');
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ============ BROADCASTS ============
  
  const loadBroadcasts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/chat/broadcasts/my?token=${token}`);
      setBroadcasts(response.data.broadcasts || []);
      setBroadcastsToday(response.data.broadcasts_today || 0);
      setRemainingToday(response.data.remaining_today || 10);
    } catch (error) {
      console.error('Error loading broadcasts:', error);
    }
  }, []);

  const sendBroadcast = async () => {
    if (!newBroadcast.content.trim()) {
      toast.error('Digite uma mensagem');
      return;
    }

    if (remainingToday <= 0) {
      toast.error('Limite diário de broadcasts atingido');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('token', token);
      formData.append('content', newBroadcast.content);
      formData.append('target', 'all');
      
      if (newBroadcast.postId) {
        formData.append('post_id', newBroadcast.postId);
      }
      
      if (newBroadcast.image) {
        formData.append('image', newBroadcast.image);
      }

      const response = await axios.post(`${API}/api/chat/broadcast`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`📢 Anúncio enviado para ${response.data.recipients_count} usuários!`);
      setNewBroadcast({ content: '', image: null, postId: '' });
      setRemainingToday(response.data.remaining_today);
      loadBroadcasts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar anúncio');
    } finally {
      setLoading(false);
    }
  };

  // ============ ASSINANTES ============

  const loadSubscribers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/chat/subscribers/list?token=${token}`);
      setSubscribers(response.data.subscribers || []);
    } catch (error) {
      console.error('Error loading subscribers:', error);
    }
  }, []);

  const loadSubscriberChat = async (subscriberId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/messages/chat/${subscriberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriberMessages(response.data.messages || []);
      setSelectedSubscriber(subscribers.find(s => s.id === subscriberId));
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error loading subscriber chat:', error);
    }
  };

  const sendSubscriberMessage = async (toAll = false) => {
    if (!newSubscriberMessage.trim()) {
      toast.error('Digite uma mensagem');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('token', token);
      formData.append('content', newSubscriberMessage);
      
      if (!toAll && selectedSubscriber) {
        formData.append('subscriber_id', selectedSubscriber.id);
      }

      const response = await axios.post(`${API}/api/chat/subscribers/message`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`💌 Mensagem enviada para ${response.data.messages_sent} assinante(s)!`);
      setNewSubscriberMessage('');
      
      if (selectedSubscriber) {
        loadSubscriberChat(selectedSubscriber.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setLoading(false);
    }
  };

  // ============ CHAT PRIVADO ============

  const loadPrivateChatRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [requestsRes, activeRes] = await Promise.all([
        axios.get(`${API}/api/chat/private/requests?token=${token}&role=creator`),
        axios.get(`${API}/api/chat/private/active?token=${token}`)
      ]);
      
      setPrivateChatRequests(requestsRes.data.requests?.filter(r => r.status !== 'active') || []);
      setActivePrivateChats(activeRes.data.active_chats || []);
    } catch (error) {
      console.error('Error loading private chat requests:', error);
    }
  }, []);

  const setPrivateChatPrice = async (requestId) => {
    const price = parseFloat(priceInput);
    if (!price || price <= 0) {
      toast.error('Digite um preço válido');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('token', token);
      formData.append('chat_request_id', requestId);
      formData.append('price', price.toString());

      await axios.post(`${API}/api/chat/private/set-price`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`💰 Preço de R$${price.toFixed(2)} definido!`);
      setPriceInput('');
      loadPrivateChatRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao definir preço');
    } finally {
      setLoading(false);
    }
  };

  const loadPrivateChatMessages = async (chatRequestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/chat/private/messages/${chatRequestId}?token=${token}`);
      setPrivateMessages(response.data.messages || []);
      setSelectedPrivateChat(response.data.chat_request);
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error loading private messages:', error);
    }
  };

  const sendPrivateMessage = async () => {
    if (!newPrivateMessage.trim() || !selectedPrivateChat) {
      toast.error('Digite uma mensagem');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('token', token);
      formData.append('chat_request_id', selectedPrivateChat.id);
      formData.append('content', newPrivateMessage);

      await axios.post(`${API}/api/chat/private/message`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setNewPrivateMessage('');
      loadPrivateChatMessages(selectedPrivateChat.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setLoading(false);
    }
  };

  // ============ EFFECTS ============

  useEffect(() => {
    if (activeTab === 'broadcasts') {
      loadBroadcasts();
    } else if (activeTab === 'subscribers') {
      loadSubscribers();
    } else if (activeTab === 'private') {
      loadPrivateChatRequests();
    }
  }, [activeTab, loadBroadcasts, loadSubscribers, loadPrivateChatRequests]);

  // Polling para mensagens
  useEffect(() => {
    let interval;
    if (selectedSubscriber) {
      interval = setInterval(() => loadSubscriberChat(selectedSubscriber.id), 5000);
    } else if (selectedPrivateChat) {
      interval = setInterval(() => loadPrivateChatMessages(selectedPrivateChat.id), 5000);
    }
    return () => clearInterval(interval);
  }, [selectedSubscriber, selectedPrivateChat]);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API}${url}`;
  };

  return (
    <div className="bg-[#0D0D0D] min-h-screen text-white">
      {/* Header com Tabs */}
      <div className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1 py-2">
            <button
              onClick={() => { setActiveTab('broadcasts'); setSelectedSubscriber(null); setSelectedPrivateChat(null); }}
              className={`px-4 py-3 rounded-t-lg font-semibold transition-all ${
                activeTab === 'broadcasts'
                  ? 'bg-[#C79F5E] text-black'
                  : 'bg-white/5 hover:bg-white/10 text-gray-300'
              }`}
            >
              📢 Anúncios
              <span className="ml-2 text-xs opacity-70">({remainingToday}/10)</span>
            </button>
            <button
              onClick={() => { setActiveTab('subscribers'); setSelectedPrivateChat(null); }}
              className={`px-4 py-3 rounded-t-lg font-semibold transition-all ${
                activeTab === 'subscribers'
                  ? 'bg-[#C79F5E] text-black'
                  : 'bg-white/5 hover:bg-white/10 text-gray-300'
              }`}
            >
              👥 Assinantes
              <span className="ml-2 text-xs opacity-70">({subscribers.length})</span>
            </button>
            <button
              onClick={() => { setActiveTab('private'); setSelectedSubscriber(null); }}
              className={`px-4 py-3 rounded-t-lg font-semibold transition-all ${
                activeTab === 'private'
                  ? 'bg-[#C79F5E] text-black'
                  : 'bg-white/5 hover:bg-white/10 text-gray-300'
              }`}
            >
              💎 Chat Privado
              {privateChatRequests.filter(r => r.status === 'pending').length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {privateChatRequests.filter(r => r.status === 'pending').length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* ============ TAB: BROADCASTS ============ */}
        {activeTab === 'broadcasts' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Criar Novo Broadcast */}
            <div className="bg-[#1A1A1A] rounded-2xl p-6 border border-[#C79F5E]/20">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                📢 Novo Anúncio
              </h2>
              
              <textarea
                value={newBroadcast.content}
                onChange={(e) => setNewBroadcast(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Escreva seu anúncio para todos os usuários..."
                className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white placeholder-gray-500 resize-none h-32 focus:border-[#C79F5E] outline-none"
                maxLength={2000}
              />
              
              <div className="flex items-center gap-3 mt-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={(e) => setNewBroadcast(prev => ({ ...prev, image: e.target.files[0] }))}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all flex items-center gap-2"
                >
                  📷 {newBroadcast.image ? 'Trocar Foto' : 'Adicionar Foto'}
                </button>
                
                {newBroadcast.image && (
                  <span className="text-sm text-gray-400">
                    {newBroadcast.image.name}
                  </span>
                )}
              </div>
              
              <input
                type="text"
                value={newBroadcast.postId}
                onChange={(e) => setNewBroadcast(prev => ({ ...prev, postId: e.target.value }))}
                placeholder="ID do post para vincular (opcional)"
                className="w-full mt-4 bg-white/5 border border-white/10 rounded-xl p-3 text-white placeholder-gray-500 focus:border-[#C79F5E] outline-none"
              />
              
              <button
                onClick={sendBroadcast}
                disabled={loading || !newBroadcast.content.trim() || remainingToday <= 0}
                className="w-full mt-4 py-3 bg-gradient-to-r from-[#C79F5E] to-[#D4AF37] text-black font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Enviando...' : `📤 Enviar para Todos (${remainingToday} restantes)`}
              </button>
            </div>

            {/* Histórico de Broadcasts */}
            <div className="bg-[#1A1A1A] rounded-2xl p-6 border border-white/10">
              <h2 className="text-xl font-bold mb-4">📜 Histórico de Anúncios</h2>
              
              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {broadcasts.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nenhum anúncio enviado ainda</p>
                ) : (
                  broadcasts.map((broadcast) => (
                    <div key={broadcast.id} className="bg-white/5 rounded-xl p-4">
                      <p className="text-white">{broadcast.content}</p>
                      {broadcast.image_url && (
                        <img
                          src={getImageUrl(broadcast.image_url)}
                          alt=""
                          className="mt-2 rounded-lg max-h-40 object-cover"
                        />
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(broadcast.created_at).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* ============ TAB: ASSINANTES ============ */}
        {activeTab === 'subscribers' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Lista de Assinantes */}
            <div className="bg-[#1A1A1A] rounded-2xl p-4 border border-white/10">
              <h2 className="text-lg font-bold mb-4">👥 Meus Assinantes</h2>
              
              {/* Enviar para Todos */}
              <button
                onClick={() => setSelectedSubscriber(null)}
                className={`w-full mb-4 p-3 rounded-xl transition-all ${
                  !selectedSubscriber ? 'bg-[#C79F5E] text-black' : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                📣 Enviar para Todos
              </button>
              
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {subscribers.map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => loadSubscriberChat(sub.id)}
                    className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all ${
                      selectedSubscriber?.id === sub.id ? 'bg-[#C79F5E]/20 border border-[#C79F5E]' : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4D1A4A] to-[#C79F5E] flex items-center justify-center overflow-hidden">
                      {sub.picture ? (
                        <img src={getImageUrl(sub.picture)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white font-bold">{sub.name?.charAt(0) || '?'}</span>
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-semibold truncate">{sub.name}</p>
                      {sub.unread_count > 0 && (
                        <span className="text-xs bg-red-500 px-2 py-0.5 rounded-full">{sub.unread_count} novas</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Área de Chat */}
            <div className="lg:col-span-2 bg-[#1A1A1A] rounded-2xl border border-white/10 flex flex-col h-[600px]">
              <div className="p-4 border-b border-white/10">
                <h2 className="font-bold">
                  {selectedSubscriber ? `💬 Chat com ${selectedSubscriber.name}` : '📣 Mensagem para Todos os Assinantes'}
                </h2>
              </div>
              
              {/* Mensagens */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {selectedSubscriber ? (
                  subscriberMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`max-w-[70%] p-3 rounded-2xl ${
                        msg.sender_id === user?.id
                          ? 'ml-auto bg-[#C79F5E] text-black'
                          : 'bg-white/10'
                      }`}
                    >
                      <p>{msg.content}</p>
                      {msg.image_url && (
                        <img src={getImageUrl(msg.image_url)} alt="" className="mt-2 rounded-lg max-w-full" />
                      )}
                      <p className="text-xs opacity-60 mt-1">
                        {new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-12">
                    <p className="text-4xl mb-4">📣</p>
                    <p>Sua mensagem será enviada para todos os {subscribers.length} assinantes</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Input */}
              <div className="p-4 border-t border-white/10">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSubscriberMessage}
                    onChange={(e) => setNewSubscriberMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendSubscriberMessage(!selectedSubscriber)}
                    placeholder="Digite sua mensagem..."
                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:border-[#C79F5E] outline-none"
                  />
                  <button
                    onClick={() => sendSubscriberMessage(!selectedSubscriber)}
                    disabled={loading || !newSubscriberMessage.trim()}
                    className="px-6 py-3 bg-[#C79F5E] text-black font-bold rounded-xl hover:opacity-90 disabled:opacity-50"
                  >
                    {loading ? '...' : '📤'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============ TAB: CHAT PRIVADO ============ */}
        {activeTab === 'private' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Solicitações Pendentes e Chats Ativos */}
            <div className="bg-[#1A1A1A] rounded-2xl p-4 border border-white/10">
              {/* Solicitações Pendentes */}
              {privateChatRequests.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-bold mb-3 text-yellow-400">⏳ Solicitações Pendentes</h3>
                  <div className="space-y-2">
                    {privateChatRequests.map((req) => (
                      <div key={req.id} className="bg-white/5 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#4D1A4A] to-[#C79F5E] flex items-center justify-center">
                            {req.user_picture ? (
                              <img src={getImageUrl(req.user_picture)} alt="" className="w-full h-full object-cover rounded-full" />
                            ) : (
                              <span className="text-white text-sm font-bold">{req.user_name?.charAt(0)}</span>
                            )}
                          </div>
                          <span className="font-semibold">{req.user_name}</span>
                        </div>
                        <p className="text-sm text-gray-400 mb-2">"{req.initial_message}"</p>
                        
                        {req.status === 'pending' && (
                          <div className="flex gap-2">
                            <input
                              type="number"
                              placeholder="R$ Preço"
                              value={priceInput}
                              onChange={(e) => setPriceInput(e.target.value)}
                              className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-sm"
                            />
                            <button
                              onClick={() => setPrivateChatPrice(req.id)}
                              className="px-3 py-2 bg-green-600 rounded-lg text-sm font-bold"
                            >
                              Definir
                            </button>
                          </div>
                        )}
                        
                        {req.status === 'priced' && (
                          <p className="text-sm text-[#C79F5E]">💰 Aguardando pagamento de R${req.price?.toFixed(2)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Chats Ativos */}
              <h3 className="font-bold mb-3 text-green-400">💎 Chats Ativos</h3>
              <div className="space-y-2">
                {activePrivateChats.length === 0 ? (
                  <p className="text-gray-500 text-sm">Nenhum chat ativo</p>
                ) : (
                  activePrivateChats.map((chat) => (
                    <button
                      key={chat.id}
                      onClick={() => loadPrivateChatMessages(chat.id)}
                      className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all ${
                        selectedPrivateChat?.id === chat.id ? 'bg-[#C79F5E]/20 border border-[#C79F5E]' : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4D1A4A] to-[#C79F5E] flex items-center justify-center">
                        {chat.user_picture ? (
                          <img src={getImageUrl(chat.user_picture)} alt="" className="w-full h-full object-cover rounded-full" />
                        ) : (
                          <span className="text-white font-bold">{chat.user_name?.charAt(0)}</span>
                        )}
                      </div>
                      <div className="text-left">
                        <p className="font-semibold">{chat.user_name}</p>
                        <p className="text-xs text-[#C79F5E]">R${chat.price?.toFixed(2)}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Área de Chat Privado */}
            <div className="lg:col-span-2 bg-[#1A1A1A] rounded-2xl border border-white/10 flex flex-col h-[600px]">
              {selectedPrivateChat ? (
                <>
                  <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <h2 className="font-bold">💎 Chat com {selectedPrivateChat.user_name}</h2>
                    <span className="text-sm text-[#C79F5E]">Pago: R${selectedPrivateChat.price?.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {/* Mensagem inicial */}
                    <div className="bg-white/5 p-3 rounded-2xl max-w-[70%]">
                      <p className="text-xs text-gray-400 mb-1">Mensagem inicial:</p>
                      <p>{selectedPrivateChat.initial_message}</p>
                    </div>
                    
                    {privateMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`max-w-[70%] p-3 rounded-2xl ${
                          msg.sender_id === user?.id
                            ? 'ml-auto bg-[#C79F5E] text-black'
                            : 'bg-white/10'
                        }`}
                      >
                        <p>{msg.content}</p>
                        {msg.image_url && (
                          <img src={getImageUrl(msg.image_url)} alt="" className="mt-2 rounded-lg max-w-full" />
                        )}
                        <p className="text-xs opacity-60 mt-1">
                          {new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                  
                  <div className="p-4 border-t border-white/10">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newPrivateMessage}
                        onChange={(e) => setNewPrivateMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendPrivateMessage()}
                        placeholder="Digite sua mensagem..."
                        className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:border-[#C79F5E] outline-none"
                      />
                      <button
                        onClick={sendPrivateMessage}
                        disabled={loading || !newPrivateMessage.trim()}
                        className="px-6 py-3 bg-[#C79F5E] text-black font-bold rounded-xl hover:opacity-90 disabled:opacity-50"
                      >
                        {loading ? '...' : '📤'}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <p className="text-6xl mb-4">💎</p>
                    <p>Selecione um chat privado para conversar</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorChatDashboard;
